import React from 'react';
import type { useApplicationDetail } from '../../hooks/useApplicationDetail';

interface Props { h: ReturnType<typeof useApplicationDetail>; }

export const EmploymentEditSection: React.FC<Props> = ({ h }) => {
  const { editedEmployment } = h;

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold flex items-center">
          Doświadczenie Zawodowe
          <span className="ml-3 text-sm bg-blue-100 text-blue-700 px-3 py-1 rounded-full">Zarządzanie</span>
        </h2>
        <button onClick={h.handleAddEmployment} className="bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-600 flex items-center text-sm">
          + Dodaj pracę
        </button>
      </div>
      <div className="space-y-4">
        {editedEmployment.length === 0 ? (
          <p className="text-gray-500 italic">Brak wpisów. Kliknij "Dodaj pracę" aby dodać doświadczenie zawodowe.</p>
        ) : (
          editedEmployment.map((emp, index) => (
            <div key={index} className="border-2 border-gray-200 rounded-lg p-4 bg-gray-50">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-gray-700">Zatrudnienie #{index + 1}</h3>
                <button onClick={() => h.handleRemoveEmployment(index)} className="text-red-600 hover:text-red-800 font-medium text-sm">Usuń</button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nazwa firmy</label>
                  <input type="text" value={emp.company_name || ''} onChange={(e) => h.handleEmploymentChange(index, 'company_name', e.target.value)} className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500" placeholder="np. Firma ABC Sp. z o.o." />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">NIP firmy</label>
                  <input type="text" value={emp.company_nip || ''} onChange={(e) => h.handleEmploymentChange(index, 'company_nip', e.target.value)} className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500" placeholder="123-456-78-90" />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Stanowisko</label>
                  <input type="text" value={emp.position || ''} onChange={(e) => h.handleEmploymentChange(index, 'position', e.target.value)} className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500" placeholder="np. Specjalista ds. IT" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Data od</label>
                  <input type="date" value={emp.period_from || ''} onChange={(e) => h.handleEmploymentChange(index, 'period_from', e.target.value)} className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Data do</label>
                  <input type="date" value={emp.period_to || ''} onChange={(e) => h.handleEmploymentChange(index, 'period_to', e.target.value)} className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500" />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Opis obowiązków</label>
                  <textarea value={emp.job_description || ''} onChange={(e) => h.handleEmploymentChange(index, 'job_description', e.target.value)} className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500" rows={3} placeholder="Krótki opis wykonywanej pracy..." />
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export const EmploymentViewSection: React.FC<Props> = ({ h }) => {
  const { application } = h;
  const employment = application?.employment || [];

  const formatDate = (d: string) => {
    if (!d) return '—';
    try { return new Date(d).toLocaleDateString('pl-PL', { year: 'numeric', month: 'short' }); }
    catch { return d; }
  };

  const calcDuration = (from: string, to: string) => {
    if (!from) return null;
    try {
      const start = new Date(from);
      const end = to ? new Date(to) : new Date();
      const months = (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth());
      if (months < 1) return '<1 mies.';
      const y = Math.floor(months / 12);
      const m = months % 12;
      if (y === 0) return `${m} mies.`;
      if (m === 0) return `${y} ${y === 1 ? 'rok' : y < 5 ? 'lata' : 'lat'}`;
      return `${y} ${y === 1 ? 'rok' : y < 5 ? 'lata' : 'lat'} ${m} mies.`;
    } catch { return null; }
  };

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold flex items-center">
          Historia Zatrudnienia
          <span className="ml-3 text-sm bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full">{employment.length}</span>
        </h2>
      </div>
      {employment.length === 0 ? (
        <p className="text-gray-400 italic text-sm">Brak wpisów o zatrudnieniu. Klient może je dodać przez portal lub operator w trybie edycji.</p>
      ) : (
        <div className="space-y-4">
          {employment.map((emp: any, idx: number) => (
            <div key={idx} className="border-l-4 border-emerald-500 pl-4 py-3 bg-emerald-50/30 rounded-r-lg">
              <div className="flex items-start justify-between">
                <div>
                  <p className="font-semibold text-gray-900">{emp.position || 'Stanowisko nieznane'}</p>
                  <p className="text-sm text-gray-700">{emp.company_name || 'Firma nieznana'}</p>
                </div>
                <div className="text-right ml-2">
                  <span className="text-xs text-gray-500 whitespace-nowrap">
                    {formatDate(emp.period_from)} — {emp.period_to ? formatDate(emp.period_to) : 'obecnie'}
                  </span>
                  {calcDuration(emp.period_from, emp.period_to) && (
                    <p className="text-xs font-semibold text-emerald-600">{calcDuration(emp.period_from, emp.period_to)}</p>
                  )}
                </div>
              </div>
              <div className="flex flex-wrap gap-2 mt-2">
                {emp.company_nip && (
                  <span className="inline-block text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">NIP: {emp.company_nip}</span>
                )}
              </div>
              {emp.job_description && (
                <p className="text-sm text-gray-600 mt-2 leading-relaxed">{emp.job_description}</p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
